import React, { Component, Fragment } from 'react'
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap'
import PropTypes from 'prop-types'
import TabIndicator from './TabIndicator'
import { ThemedComponent } from '../ThemedComponent/ThemedComponent'
import Icon from '../Icon/Icon'
import { v4 as uuid } from 'uuid'

const BORDER_STYLES = ['dotted', 'dashed', 'solid', 'none']
const LAYOUT = ['horizontal', 'vertical']
const ALIGN = ['center', 'start', 'end']
const color = PropTypes.color

const theme = {
  container: PropTypes.shape({
    borderWidth: PropTypes.string,
    borderColor: color,
    borderStyle: PropTypes.oneOf(BORDER_STYLES),
    borderRadius: PropTypes.string,
    padding: PropTypes.string,
    margin: PropTypes.string,
    shadow: PropTypes.string,
    tabHeadersBackgroundColor: color
  }),
  tabHeaders: PropTypes.shape({
    compactTabs: PropTypes.bool,
    backgroundColor: color,
    color: color,
    activeBackgroundColor: color,
    activeColor: color,
    hoverBackgroundColor: color,
    hoverColor: color,
    tabsWidth: PropTypes.string,
    tabsAlign: PropTypes.oneOf(ALIGN),
    labelAlign: PropTypes.oneOf(ALIGN),
    labelWhiteSpace: PropTypes.oneOf(['normal', 'nowrap']),
    borderWidth: PropTypes.string,
    borderColor: color,
    borderStyle: PropTypes.oneOf(BORDER_STYLES),
    borderRadius: PropTypes.string,
    indicator: PropTypes.bool,
    indicatorWidth: PropTypes.string,
    indicatorPosition: PropTypes.oneOf(['start', 'end']),
    indicatorColor: color
  }),
  tabContent: PropTypes.shape({
    backgroundColor: color,
    padding: PropTypes.string,
    borderWidth: PropTypes.string,
    borderColor: color,
    borderStyle: PropTypes.oneOf(BORDER_STYLES),
    borderRadius: PropTypes.string
  })
}

const Tabs = ThemedComponent(class Tabs extends Component {
  static propTypes = {
    /**
     * Allows the user to determine to choose between horizontal tabs or vertical side tabs.
     */
    tabsLayout: PropTypes.oneOf(LAYOUT),

    /**
     * Controls the visual properties of the tab component.
     */
    styling: PropTypes.shape(theme),

    /**
     * Determines which tab to display on load.
     */
    defaultSelectedTab: PropTypes.string,

    /**
     * Function triggered on tab change.
     */
    handleTabChange: PropTypes.func,

    /**
     * Automatically scroll to the top of the tab on tab change, if it is not already in view.
     */
    onChangeScroll: PropTypes.bool,

    /**
     * Enable smooth scrolling transition when scrolling to top of tab on change.
     */
    onChangeScrollTransition: PropTypes.bool
  }

  static defaultProps = {
    tabsLayout: 'horizontal',
    defaultSelectedTab: '1',
    onChangeScroll: true,
    onChangeScrollTransition: true,
    styling: {
      tabHeaders: {
        compactTabs: false,
        backgroundColor: '#fff',
        color: '#212529',
        tabsAlign: 'start',
        labelAlign: 'center',
        labelWhiteSpace: 'nowrap',
        borderColor: '#dee2e6',
        borderStyle: 'solid',
        indicator: false,
        indicatorWidth: '4px',
        indicatorPosition: 'start',
        indicatorColor: '#dee2e6'
      },
      tabContent: {
        backgroundColor: '#fff',
        padding: '.5rem 0 0'
      }
    }
  }

  constructor (props) {
    super(props)

    const isDefaultSelectedTabAName = props.defaultSelectedTab && isNaN(parseInt(props.defaultSelectedTab))
    const defaultTabIndex = isDefaultSelectedTabAName ? this.props?.tabs?.findIndex(t => t.name === props.defaultSelectedTab) + 1 : parseInt(props.defaultSelectedTab)
    const currentSelectedTab = props._component?.state?.selectedTab ?? (defaultTabIndex >= 1 ? defaultTabIndex : 1)
    this.state = {
      selectedTab: currentSelectedTab,
      changeTab: async (nameOrIndex) => this.onChangeTab(nameOrIndex),
      key: this.props.key || uuid(),
    }

    this.navRef = React.createRef()
    this.navLinksRef = React.createRef()
  }

  onChangeTab = (nameOrIndex) => {
    const isTabName = isNaN(parseInt(nameOrIndex))
    let tabIndex = isTabName ? this.props?.tabs?.findIndex(t => t.name === nameOrIndex) + 1 : nameOrIndex
    tabIndex = tabIndex >= 1 ? tabIndex : 1
    this.setState({ selectedTab: tabIndex })
    
    if (this.props.handleTabChange) this.props.handleTabChange(nameOrIndex)

    if (this.props.onChangeScroll && this.navRef && this.navRef.current && !this.isInViewport(this.navRef.current)) {
      this.navRef.current.scrollIntoView({ behavior: this.props.onChangeScrollTransition ? 'smooth' : 'auto' })
    }
  }

  isInViewport = (element) => {
    const rect = element.getBoundingClientRect()
    return (
      rect.top >= 0
    )
  }

  renderTabs (tabs) {
    const { theme, styling: { tabHeaders } = {} } = this.props
    const { selectedTab, key } = this.state
    const currentTab = selectedTab > tabs?.length ? 1 : selectedTab
    return (
      <Fragment>
        <TabIndicator defaultSelectedTab={currentTab} type='scoped' onChangeTab={this.onChangeTab} selectedTab={currentTab} theme={theme}>
          {tabs && Array.isArray(tabs) && tabs.map((tab, index) => {
            let tabProps = {}
            Array.isArray(tab?.mapping?.children) && tab.mapping.children.forEach(tabProp => {
              const { value, key } = tabProp
              if (value !== undefined) {
                tabProps = { [key]: value, ...tabProps }
              }
            })
            const { icon, iconPlacement, iconSize, name } = tabProps
            return (
              <NavItem key={`nav-${key}-${index}`}>
                <NavLink
                  className={`${tabHeaders && tabHeaders.indicator ? `indicator` : ``}  ${currentTab === (index + 1) ? 'active' : ''}`}
                >
                  {icon && iconPlacement === `start` && <Icon icon={icon} iconSize={iconSize} />}
                  <label>
                    {name || (!!tab.props && tab.props.label) || tab.name}
                  </label>
                  {icon && iconPlacement === `end` && <Icon icon={icon} iconSize={iconSize} />}
                </NavLink>
              </NavItem>
            )
          }
          )}
        </TabIndicator>
      </Fragment>
    )
  }

  render () {
    const {
      tabs,
      children,
      tabsLayout,
      styling: {
        headerBackground,
        tabHeaderBackground,
        tabHeaderColor,
        tabHeaderActiveBackground,
        tabHeaderActiveColor,
        tabHeaderHoverBackground,
        tabHeaderHoverColor,
        tabsWidth,
        labelWhiteSpace,
        container,
        tabHeaders,
        tabContent
      } = {}
    } = this.props
    const { selectedTab, key } = this.state
    const currentTab = selectedTab > tabs.length ? 1 : selectedTab
    const isVerticalLayout = tabsLayout === 'vertical'
    return (
      <div ref={this.navRef} className={`h-100 d-flex nav-cards ${isVerticalLayout ? 'side-tabs' : ''}`}>
        <div>
          <Nav className='card-headers' tabs>
            {tabs && this.renderTabs(tabs)}
          </Nav>
        </div>
        <TabContent activeTab={currentTab}>
          {children && Array.isArray(children) && children.map((child, index) => {
            if (child && child.position) child.position.height -= 2
            return (
              <TabPane key={`tab-${key}-${index}`} tabId={index + 1}>
                {(currentTab === index + 1) && child}
              </TabPane>
            )
          })}
        </TabContent>
        <style jsx>{`
          div :global(label) {
            cursor: pointer;
            vertical-align: text-bottom;
            margin-bottom: 0;
          }
          i {
            vertical-align: middle;
          }
          div.nav-cards :global(.card-headers li.nav-item) {
            flex: ${tabHeaders && tabHeaders.compactTabs ? 0 : 1} 0 ${((tabsWidth || (tabHeaders && tabHeaders.tabsWidth))) || `auto`};
            margin-bottom: -1px;
          }
          div.nav-cards.side-tabs :global(.card-headers li.nav-item) {
            flex: 0 1 auto;
          }
          .nav-link label,
          .nav-link .zmdi {
            color: #fff;
          }
          .nav-link.active label,
          .nav-link.active .zmdi {
            color: inherit !important;
          }
          div :global(.card-headers) {
            justify-content: ${tabHeaders && tabHeaders.tabsAlign === `center` ? `center` : `flex-${(tabHeaders && tabHeaders.tabsAlign) || `start`}`};
            flex-direction: ${isVerticalLayout ? `column` : `row`};
          }
          div :global(.card-headers),
          div :global(.nav-cards) {
            background-color: ${headerBackground || (container && container.tabHeadersBackgroundColor) || `transparent`} !important;
          }
          div.nav-cards {
            flex-direction: ${isVerticalLayout ? `row` : `column`};
            border-width: ${(container && container.borderWidth) || 0};
            border-style: ${(container && container.borderStyle) || `none`};
            border-color: ${(container && container.borderColor) || `transparent`};
            border-radius: ${(container && container.borderRadius) || 0};
            padding: ${(container && container.padding) || 0};
            margin: ${(container && container.padding) || 0};
          }
          div :global(.nav-link) {
            color: ${tabHeaderColor || (tabHeaders && tabHeaders.color) || `inherit`};
            background: ${tabHeaderBackground || (tabHeaders && tabHeaders.backgroundColor) || `#fff`};
            display: flex;
            align-items: center;
            justify-content: ${tabHeaders && tabHeaders.labelAlign && tabHeaders.labelAlign !== `center` ? `flex-${(tabHeaders && tabHeaders.labelAlign)}` : `center`};
            border-width: ${(tabHeaders && tabHeaders.borderWidth) || (isVerticalLayout ? '1px 0 0' : '1px')};
            border-style: ${(tabHeaders && tabHeaders.borderStyle) || `solid`};
            border-color: ${(tabHeaders && tabHeaders.borderColor) || `#dee2e6`};
            border-radius: ${(tabHeaders && tabHeaders.borderRadius) || (isVerticalLayout ? 0 : `.25rem .25rem 0 0`)};
          }
          div :global(.nav-item:not(:first-child) .nav-link) {
            border-left-width: 0;
          }
          div.side-tabs :global(.nav-item:last-child .nav-link) {
            border-bottom-width: 1px;
          }
          div :global(.nav-link.active) {
            background-color: ${tabHeaderActiveBackground || (tabHeaders && tabHeaders.activeBackgroundColor) || `#fff`};
            color: ${tabHeaderActiveColor || (tabHeaders && tabHeaders.activeColor) || `inherit`};
          }
          div.side-tabs :global(.nav-link.active) {
            margin-right: -1px;
          }
          div.side-tabs :global(.nav-link),
          div.side-tabs :global(.nav-link.active) {
            margin-bottom: 0;
          }
          div :global(.nav-link.active.indicator) {
            box-shadow: ${
      isVerticalLayout
        ? `inset ${tabHeaders && tabHeaders.indicatorPosition === `end` ? `-` : ``}${(tabHeaders && tabHeaders.indicatorWidth) || `4px`} 0 0 0 ${(tabHeaders && tabHeaders.indicatorColor) || `#dee2e6`};`
        : `inset 0 ${tabHeaders && tabHeaders.indicatorPosition === `end` ? `-` : ``}${(tabHeaders && tabHeaders.indicatorWidth) || `4px`} 0 0 ${(tabHeaders && tabHeaders.indicatorColor) || `#dee2e6`};`
      }
          }
          div :global(.nav-link:hover) {
            background-color: ${tabHeaderHoverBackground || (tabHeaders && tabHeaders.hoverBackgroundColor) || `#fff`};
            color: ${tabHeaderHoverColor || (tabHeaders && tabHeaders.hoverColor) || `inherit`};
          }
          div :global(.tab-content) {
            height: 100%;
            padding: ${(tabContent && tabContent.padding) || 0};
            background: ${(tabContent && tabContent.backgroundColor) || `#fff`};
            border-width: ${(tabContent && tabContent.borderWidth) || (isVerticalLayout  ? '1px 1px 1px 0' : '0 1px 1px')};
            border-style: ${(tabContent && tabContent.borderStyle) || `solid`};
            border-color: ${(tabContent && tabContent.borderColor) || `#dee2e6`};
            border-radius: ${(tabContent && tabContent.borderRadius) || (isVerticalLayout ? '0 .25rem .25rem 0' : '0 0 .25rem .25rem')};
          }
          div :global(.nav-tabs) {
            border-bottom: ${`1px ${(tabHeaders && tabHeaders.borderStyle) || `solid`} ${(tabHeaders && tabHeaders.borderColor) || `#dee2e6`}`};
          }
          div :global(.nav-tabs .nav-item .nav-link) {
              ${(!isVerticalLayout && `height: 100%;`) || ``}
          }
          div.nav-cards.side-tabs :global(.nav-tabs) {
              width: ${tabsWidth || (tabHeaders && tabHeaders.tabsWidth) || (isVerticalLayout ? 'min-content' : 'fit-content')};
          }
          div.nav-cards.side-tabs :global(.nav-tabs .nav-item) {
              display: block !important;
              width: 100% !important;
              white-space: ${labelWhiteSpace};
          }
          div.nav-cards :global(.nav-tabs .nav-item a.nav-link.active) {
              border-color: ${(tabHeaders && tabHeaders.borderColor) || `#dee2e6`} ${(tabHeaders && tabHeaders.borderColor) || `#dee2e6`} transparent;
          }
          div.nav-cards.side-tabs :global(.nav-tabs .nav-item a.nav-link.active) {
              border-color: ${(tabHeaders && tabHeaders.borderColor) || `#dee2e6`} !important;
          }
          div.nav-cards.side-tabs :global(.nav-tabs) {
            border: ${`1px ${(tabHeaders && tabHeaders.borderStyle) || `none`} ${(tabHeaders && tabHeaders.borderColor) || `transparent`}`};
            border-top-width: ${tabHeaders && tabHeaders.tabsAlign === 'start' ? 0 : '1px'};
            border-bottom-width: ${tabHeaders && tabHeaders.tabsAlign === 'end' ? 0 : '1px'};
            border-radius: ${tabHeaders && tabHeaders.borderRadius || '.25rem 0 0 .25rem'};
          }
          div.nav-cards.side-tabs :global(.tab-content){
            width: 100%;
          }
        `}</style>
      </div>
    )
  }
})

export default Tabs
